/* eslint-disable no-restricted-syntax */
/* eslint-disable guard-for-in */
import { createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

import axiosInstance from "../../../../apiConfigs/axiosInstance";
import { ERROR__TOASTER } from "../../../../helpers/utils/TosterError";
import { renderDynamicUrlFromState } from "../../../../helpers/utils/urlHelper";

export interface InterviewLogsParams {
  page?: number;
  limit?: number;
  search?: string;
  user?: string;
  team?: string;
  tag?: string;
  from?: string;
  to?: string;
  subdomain?: string;
}

export interface InterviewLogsResponse {
  success: boolean;
  data: any[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  stats: {
    totalInterviews: number;
    uniqueUsers: number;
    completedInterviews: number;
    avgPerformance: number;
  };
}

export const fetchInterviewLogs = createAsyncThunk(
  "interviewLogs/fetchInterviewLogs",
  async (
    params: InterviewLogsParams,
    { dispatch, rejectWithValue, getState }
  ) => {
    try {
      const state = getState();
      const dynamic_url = renderDynamicUrlFromState(state);

      const queryParams = new URLSearchParams();

      // Add pagination parameters
      if (params.page) {
        queryParams.append("page", params.page.toString());
      }
      if (params.limit) {
        queryParams.append("limit", params.limit.toString());
      }

      // Determine subdomain based on environment
      const host: any = window.location.hostname;
      const checkhost = host.split(".");
      const isStaging =
        process.env.NODE_ENV === "production" && checkhost[0] === "staging";
      const isDev = process.env.NODE_ENV === "development";
      const isLive = process.env.NODE_ENV === "production" && !isStaging;

      // if (isDev || isStaging) {
      //   queryParams.append("subdomain", "theagileacademy");
      // } else if (isLive) {
      //   queryParams.append("subdomain", "aa");
      // }

      queryParams.append("subdomain", "aa");

      // Add optional parameters
      if (params.search) queryParams.append("search", params.search);
      if (params.user) queryParams.append("user", params.user);
      if (params.team) queryParams.append("team", params.team);
      if (params.tag) queryParams.append("tag", params.tag);
      if (params.from) queryParams.append("from", params.from);
      if (params.to) queryParams.append("to", params.to);

      const url = `https://backend.joindojo.ai/api/company-user/interviews/public/all?${queryParams.toString()}`;

      const response = await axiosInstance.get(url);

      if (
        response.data &&
        response.data.data &&
        response.data.data.interviews
      ) {
        // Transform the data to match our interface
        const transformedData = response.data.data.interviews.map(
          (interview: any) => ({
            id: interview.id,
            userId: interview.userId || interview.user?.id,
            user: {
              name:
                interview.userFirstName && interview.userLastName
                  ? `${interview.userFirstName} ${interview.userLastName}`
                  : interview.userFirstName || "Unknown User",
              email: interview.user?.email || "",
              avatar: interview.user?.image || interview.user?.avatar,
            },
            interviewType:
              interview.interviewType || interview.role || "Interview",
            score: interview.overallScore || interview.score || 0,
            duration: interview.duration || "0m 0s",
            date: interview.createdAt || interview.date,
            status: interview.status || "completed",
            team: interview.teamName || "General",
            role: interview.role || "Interview",
            company: interview.company,
            overall_score: interview.overallScore || interview.score,
            createdAt: interview.createdAt,
            updatedAt: interview.updatedAt,
          })
        );

        // Use pagination data from the API response
        const pagination = response.data.data.pagination || {};

        return {
          success: true,
          data: transformedData,
          total: pagination.totalCount || transformedData.length,
          page: pagination.currentPage || params.page || 1,
          limit: pagination.itemsPerPage || params.limit || 10,
          totalPages:
            pagination.totalPages ||
            Math.ceil(
              (pagination.totalCount || transformedData.length) /
                (pagination.itemsPerPage || params.limit || 10)
            ),
          stats: response.data.data.stats,
        } as InterviewLogsResponse;
      }
      return rejectWithValue(
        response.data?.message || "Failed to fetch interview logs"
      );
    } catch (error: any) {
      ERROR__TOASTER(error);
      if (!error.response) {
        throw error;
      }
      return rejectWithValue(
        error?.response?.data?.message || "Failed to fetch interview logs"
      );
    }
  }
);
